import { Draft } from 'immer'

import { applyChange, Change } from 'src/hooks/DTOEditor'
import { BaseTableDTO } from 'src/models/dto/items/BaseTableDTO'
import { ItemDTO, ItemType } from 'src/models/dto/items/ItemDTO'
import { Medias } from 'src/models/dto/items/MediaDTO'
import {
    defaultMultipleChoiceItemDTO,
    MultipleChoiceDisplayType,
    MultipleChoiceItemDTO,
    MultipleChoiceResponseDTO,
    MultipleChoiceType,
} from 'src/models/dto/items/MultipleChoiceItemDTO'
import { Locale, LocalizedAttribute, LocalizeDefault } from 'src/models/dto/Locale'
import {
    factories,
    ITEM_ENTITY_STORE_SELECTOR,
} from 'src/services/EntityServices/ItemEntityService'
import { TableOperations } from 'src/services/EntityServices/ItemUpdateHandlers/TableOperations'
import { Stores } from 'src/services/Store'

const itemType = ItemType.MultipleChoice

export class MultipleChoiceHandler extends TableOperations<BaseTableDTO> {
    public static readonly INSTANCE = new MultipleChoiceHandler()

    private constructor() {
        super()
    }

    static init() {
        // add the create function to the set of factories available in ItemEntityService
        factories.set(itemType, () => defaultMultipleChoiceItemDTO())
    }

    private static store() {
        return Stores.get<MultipleChoiceItemDTO, ItemDTO>(ITEM_ENTITY_STORE_SELECTOR)
    }

    public getEntity(id: string): MultipleChoiceItemDTO {
        if (MultipleChoiceHandler.store().has(id)) {
            return MultipleChoiceHandler.store().get(id)
        } else {
            throw new Error(`entity ${id} does not exist in ${ITEM_ENTITY_STORE_SELECTOR}`)
        }
    }

    private produce(id: string, update: (draft: Draft<MultipleChoiceItemDTO>) => void) {
        MultipleChoiceHandler.store().produce<MultipleChoiceItemDTO>(id, update)
    }

    private assign(id: string, obj: Partial<MultipleChoiceItemDTO>) {
        MultipleChoiceHandler.store().assign(id, obj)
    }

    public updateLabel(id: string, label: string) {
        this.assign(id, { label })
    }

    public updateOptional(id: string, optional: boolean) {
        this.assign(id, { optional })
    }

    public updatePreserveOrder(id: string, preserveOrder: boolean) {
        this.assign(id, { preserveOrder })
    }

    public updateQuestion(id: string, locale: Locale, question: string) {
        this.produce(id, (entity) => {
            entity.questionI18N[locale] = question
        })
    }

    public updateMultipleChoiceType(id: string, multipleChoiceType: MultipleChoiceType) {
        this.produce(id, (entity) => {
            entity.multipleChoiceType = multipleChoiceType
            this.checkAndFixNumRequiredResponses(entity)
        })
    }

    public updateDisplayType(id: string, displayType: MultipleChoiceDisplayType) {
        if (!MultipleChoiceHandler.showFreeTextResponse(displayType)) {
            this.updateFreeTextCheckbox(id, false)
        }

        if (displayType === MultipleChoiceDisplayType.Image) {
            this.removeExtraResponses(id)
        }

        this.removeResponseImages(id)

        if (displayType === MultipleChoiceDisplayType.TABLE) {
            this.setNumberOfRows(id, this.getEntity(id).responses.length)
            this.setNumberOfColumns(id, 4)
            this.assign(id, { displayType })
        } else {
            this.assign(id, {
                displayType,
                tableResponses: {
                    legends: [],
                    rows: [],
                    headers: [],
                    harveyBallLegendEnabled: false,
                    symbolLegendEnabled: false,
                },
            })
        }
    }

    public updateNumRequiredResponses(
        id: string,
        numRequiredResponses?: number | null | undefined
    ) {
        this.produce(id, (entity) => {
            entity.numRequiredResponses = numRequiredResponses ?? undefined
            this.checkAndFixNumRequiredResponses(entity)
        })
    }

    public addResponse(id: string) {
        this.produce(id, ({ responses }) => {
            responses.push({
                responseI18N: LocalizeDefault(''),
                score: null,
                responseLabel: '',
            })
        })
    }

    public checkArrayBounds<T>(index: number, arr: T[]) {
        if (arr && (index < 0 || index >= arr.length)) {
            throw new Error(`Index out of bounds: Index: ${index}, Size: ${arr.length}`)
        }
    }

    private checkAndFixNumRequiredResponses(entity: Draft<MultipleChoiceItemDTO>) {
        if (entity.multipleChoiceType === MultipleChoiceType.MultiSelect) {
            if (typeof entity.numRequiredResponses === 'number') {
                if (entity.numRequiredResponses > entity.responses.length) {
                    entity.numRequiredResponses = entity.responses.length
                } else if (entity.numRequiredResponses < 1) {
                    entity.numRequiredResponses = undefined
                }
            }
        } else if (entity.multipleChoiceType === MultipleChoiceType.SingleSelect) {
            entity.numRequiredResponses = undefined
        }
    }

    public deleteResponse(id: string, index: number) {
        this.produce(id, (entity) => {
            this.checkArrayBounds(index, entity.responses)
            entity.responses.splice(index, 1)
            this.checkAndFixNumRequiredResponses(entity)
        })
    }

    public updateResponseOption(id: string, index: number, locale: Locale, response: string) {
        this.produce(id, (entity) => {
            this.checkArrayBounds(index, entity.responses)
            entity.responses[index].responseI18N[locale] = response
        })
    }

    public updateResponseLabel(id: string, index: number, response: string) {
        this.produce(id, (entity) => {
            this.checkArrayBounds(index, entity.responses)
            entity.responses[index].responseLabel = response
        })
    }

    public updateScore(id: string, index: number, score?: number | null) {
        this.produce(id, (entity) => {
            this.checkArrayBounds(index, entity.responses)
            entity.responses[index].score = score
        })
    }

    public updateResponseLocaleWiseMediaOpt(
        id: string,
        change: Change<Medias | undefined>,
        index: number
    ) {
        this.produce(id, (entity) => {
            const { localeWiseMedia } = entity.responses[index]
            entity.responses[index].localeWiseMedia = applyChange(localeWiseMedia, change)
        })
    }

    public updateResponseOrderRandomizationEnabled(
        id: string,
        responseOrderRandomizationEnabled: boolean
    ) {
        this.assign(id, { responseOrderRandomizationEnabled })
    }

    protected getTable(id: string): BaseTableDTO {
        return this.getEntity(id).tableResponses
    }

    protected produceTable(id: string, update: (draft: Draft<BaseTableDTO>) => void) {
        MultipleChoiceHandler.store().produce(id, (entity) => update(entity.tableResponses))
    }

    public setNumberOfRows(itemId: string, nextNumberOfRows: number) {
        super.setNumberOfRows(itemId, nextNumberOfRows)
        const multipleChoiceItemDTO = this.getEntity(itemId)
        const numberOfResponses = multipleChoiceItemDTO.responses.length
        const differenceInResponses = nextNumberOfRows - numberOfResponses
        if (differenceInResponses > 0) {
            for (let i = 0; i < differenceInResponses; i++) {
                this.addResponse(itemId)
            }
        } else {
            for (let i = differenceInResponses; i < 0; i++) {
                this.deleteResponse(itemId, this.getEntity(itemId).responses.length - 1)
            }
        }
    }

    public updateFreeTextCheckbox(id: string, responseEnabled: boolean) {
        this.produce(id, (entity) => {
            if (!responseEnabled) {
                entity.freeTextResponse.freeTextResponseI18N = LocalizeDefault('')
            }

            entity.freeTextResponse.responseEnabled = responseEnabled
        })
    }

    public updateFreeTextResponse(id: string, locale: Locale, response: string) {
        this.produce(id, (entity) => {
            entity.freeTextResponse.freeTextResponseI18N = {
                ...(entity.freeTextResponse?.freeTextResponseI18N ?? {}),
                [locale]: response,
            } as LocalizedAttribute<string>
        })
    }

    static showFreeTextResponse(displayType: MultipleChoiceDisplayType) {
        return (
            displayType !== MultipleChoiceDisplayType.Image &&
            displayType !== MultipleChoiceDisplayType.TABLE
        )
    }

    private removeResponseImages(id: string) {
        const entity = this.getEntity(id)

        for (let index = 0; index < entity.responses.length; index++) {
            this.updateResponseLocaleWiseMediaOpt(id, undefined, index)
        }
    }

    static checkResponsesHaveImage(responses: MultipleChoiceResponseDTO[]) {
        return responses.some((response) => {
            if (!response.localeWiseMedia) {
                return false
            }

            return response.localeWiseMedia.mediaManagerMediaVersionIds?.length || 0 > 0
        })
    }

    private removeExtraResponses(id: string) {
        const entity = this.getEntity(id)

        for (let index = entity.responses.length - 1; index > 3; index--) {
            this.deleteResponse(id, index)
        }
    }

    static checkTableLayoutUpdated(table: BaseTableDTO) {
        const headersUpdated = table.headers.some((header) => {
            return JSON.stringify(header.headerI18N) !== JSON.stringify(LocalizeDefault<string>(''))
        })

        const responsesUpdated = table.rows.some((row) => {
            return row.cells.some((cell) => {
                if (!cell) {
                    return false
                }
                return cell.dataI18N !== LocalizeDefault<string>('')
            })
        })

        return responsesUpdated || headersUpdated
    }
}
